import { useEffect, useRef, useState } from 'react';
import { Plus, RotateCcw, X as XIcon } from 'lucide-react';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Label, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/StatusBadge';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { useAuthStore } from '@/stores/auth';
import {
  inquiriesApi,
  INQUIRY_STATUS_VISUAL,
  type Inquiry,
  type InquiryStatus,
} from '@/services/inquiries.api';
import { type InsuranceCompany } from '@/services/config.api';

/**
 * Section « Compagnies interrogées » sur la fiche opportunité (demande
 * client LBC 2026-05-22).
 *
 * Affichage :
 *   - Header : titre + bouton « + Interroger » à droite (ouvre AddInquiriesModal)
 *   - Liste des inquiries avec badge statut (en attente / devis reçu / refusé)
 *   - Date d'interrogation + date refus si applicable
 *   - Boutons d'action : Marquer refusé (modale raison) / Restaurer / Supprimer
 *
 * Saisie via modale (refonte 2026-05-22, demande UX consistance Stripe) :
 *   - AddInquiriesModal s'ouvre sur clic « + Interroger »
 *   - MultiSelectDropdown des compagnies actives (filtre celles déjà interrogées)
 *   - Textarea notes optionnelle (partagée pour tout le batch)
 *   - Footer : Annuler + Interroger (N)
 *
 * Lien Quote : si un PDF de devis est uploadé pour une compagnie déjà
 * interrogée, le hook backend `maybeUpsertInquiryOnQuoteUpload` transit
 * la inquiry en `quote_received`. L'UI affiche le statut à jour après
 * refresh (déclenché par realtime ou onReload manuel après upload).
 */
export function InquiriesSection({
  opportunityId,
  companies,
  reloadKey,
}: {
  opportunityId: string;
  companies: InsuranceCompany[];
  /** Bump cette valeur pour forcer un refetch (ex. après upload Quote). */
  reloadKey?: number;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();

  const [inquiries, setInquiries] = useState<Inquiry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [refuseTarget, setRefuseTarget] = useState<Inquiry | null>(null);

  async function load() {
    try {
      const list = await inquiriesApi.list(token, opportunityId);
      setInquiries(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId, reloadKey]);

  /** Compagnies déjà interrogées sur cette opp — exclues du dropdown. */
  const alreadyInquiredIds = new Set((inquiries ?? []).map((i) => i.insuranceCompanyId));
  const availableCompanies = companies.filter((c) => c.isActive && !alreadyInquiredIds.has(c.id));

  async function handleRestorePending(inquiry: Inquiry) {
    try {
      await inquiriesApi.restorePending(token, inquiry.id);
      toast({ tone: 'success', message: `${inquiry.insuranceCompany.name} : restauré en attente.` });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  async function handleDelete(inquiry: Inquiry) {
    const ok = await confirm({
      title: 'Retirer cette compagnie ?',
      message: `« ${inquiry.insuranceCompany.name} » sera retirée des compagnies interrogées pour cette opportunité.`,
      variant: 'danger',
      confirmLabel: 'Retirer',
    });
    if (!ok) return;
    try {
      await inquiriesApi.delete(token, inquiry.id);
      toast({ tone: 'success', message: `${inquiry.insuranceCompany.name} retirée.` });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Suppression refusée',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  return (
    <Card>
      {/* Header : titre + description à gauche, bouton « + Interroger » à
          droite (refonte 2026-05-22 — demande UX consistance modale Stripe). */}
      <div className="mb-3 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <CardTitle>Compagnies interrogées</CardTitle>
          <CardDescription>
            Trace les compagnies sollicitées et leurs réponses (devis reçu ou refus).
          </CardDescription>
        </div>
        <Button
          variant="primary"
          size="sm"
          leadingIcon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => setAddModalOpen(true)}
          disabled={availableCompanies.length === 0}
          title={
            availableCompanies.length === 0
              ? 'Toutes les compagnies actives sont déjà interrogées'
              : undefined
          }
        >
          Interroger
        </Button>
      </div>

      {error && <Banner tone="danger">{error}</Banner>}

      {inquiries === null ? (
        <div className="py-3 text-sm text-text-muted">Chargement…</div>
      ) : inquiries.length === 0 ? (
        <EmptyState
          variant="list"
          title="Aucune compagnie interrogée"
          description="Cliquez sur « Interroger » en haut à droite pour sélectionner une ou plusieurs compagnies sollicitées."
        />
      ) : (
        <ul className="divide-y divide-gray-100">
          {inquiries.map((inq) => (
            <InquiryRow
              key={inq.id}
              inquiry={inq}
              onMarkRefused={() => setRefuseTarget(inq)}
              onRestore={() => void handleRestorePending(inq)}
              onDelete={() => void handleDelete(inq)}
            />
          ))}
        </ul>
      )}

      {addModalOpen && (
        <AddInquiriesModal
          opportunityId={opportunityId}
          availableCompanies={availableCompanies}
          onClose={() => setAddModalOpen(false)}
          onSubmitted={async () => {
            setAddModalOpen(false);
            await load();
          }}
        />
      )}

      {refuseTarget && (
        <RefuseInquiryModal
          inquiry={refuseTarget}
          onClose={() => setRefuseTarget(null)}
          onSubmitted={async () => {
            setRefuseTarget(null);
            await load();
          }}
        />
      )}
    </Card>
  );
}

function InquiryRow({
  inquiry,
  onMarkRefused,
  onRestore,
  onDelete,
}: {
  inquiry: Inquiry;
  onMarkRefused: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const status = INQUIRY_STATUS_VISUAL[inquiry.status];
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-brand">
            {inquiry.insuranceCompany.name}
          </span>
          <Badge tone="neutral" className={status.badge}>
            {status.label}
          </Badge>
        </div>
        <div className="mt-0.5 text-xs text-text-muted">
          Interrogée le {formatDate(inquiry.requestedAt)}
          {inquiry.quoteReceivedAt && (
            <>
              {' · '}
              <span className="text-success-600">
                Devis reçu le {formatDate(inquiry.quoteReceivedAt)}
              </span>
            </>
          )}
          {inquiry.refusedAt && (
            <>
              {' · '}
              <span className="text-danger-600">
                Refusé le {formatDate(inquiry.refusedAt)}
              </span>
            </>
          )}
        </div>
        {inquiry.refusedReason && (
          <div className="mt-1 text-xs text-text-muted">
            <span className="font-medium">Motif :</span> {inquiry.refusedReason}
          </div>
        )}
        {inquiry.notes && (
          <div className="mt-1 text-xs text-text-muted">
            <span className="font-medium">Note :</span> {inquiry.notes}
          </div>
        )}
      </div>
      <div className="flex shrink-0 gap-1">
        {/* Marquer refusé : visible si pending uniquement. quote_received ne
            peut pas devenir refused (le PDF est là, sémantique incohérente). */}
        {inquiry.status === 'pending' && (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<XIcon className="h-3.5 w-3.5" />}
            onClick={onMarkRefused}
          >
            Refusé
          </Button>
        )}
        {/* Restaurer en attente : visible uniquement si refused. */}
        {inquiry.status === 'refused' && (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}
            onClick={onRestore}
          >
            Restaurer
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onDelete}>
          Retirer
        </Button>
      </div>
    </li>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Modale Stripe-like d'ajout de compagnies interrogées en bulk (refonte
 * 2026-05-22). Pattern identique à CloseLostModal / EstimatedSigningDateModal :
 * fond brand/30 + backdrop-blur, fade-in + scale-in, focus trap, Escape,
 * footer Annuler/Confirmer.
 *
 * État local : `selectedCompanyIds` + `notes` — reset à chaque ouverture
 * (le composant est monté/démonté via `addModalOpen` côté parent). Pas de
 * pré-remplissage : le commercial choisit explicitement les compagnies.
 *
 * Note sur le MultiSelectDropdown : pas de portal, dropdown en `absolute
 * z-50` qui débordera naturellement sous la modale si la liste dépasse —
 * la modale n'a pas d'overflow-hidden donc le clipping ne survient que
 * sur le viewport. Si la liste est très longue, le MultiSelectDropdown
 * a son propre `max-h-72` interne + scroll.
 */
function AddInquiriesModal({
  opportunityId,
  availableCompanies,
  onClose,
  onSubmitted,
}: {
  opportunityId: string;
  availableCompanies: InsuranceCompany[];
  onClose: () => void;
  onSubmitted: () => Promise<void>;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (selectedCompanyIds.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      await inquiriesApi.create(token, opportunityId, {
        insuranceCompanyIds: selectedCompanyIds,
        notes: notes.trim() || undefined,
      });
      toast({
        tone: 'success',
        title: `${selectedCompanyIds.length} compagnie(s) interrogée(s)`,
        message:
          'Statut initial : en attente. Le statut passera à « devis reçu » quand un PDF sera uploadé.',
      });
      await onSubmitted();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Création impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Interroger des compagnies"
        onClick={(e) => e.stopPropagation()}
        className="shadow-modal w-full max-w-md rounded-lg bg-white animate-scale-in"
      >
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="border-border-subtle flex items-start justify-between border-b pb-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                  <Plus className="h-4 w-4" />
                </span>
                <h2 className="text-brand text-lg font-semibold">Interroger des compagnies</h2>
              </div>
              <p className="text-text-muted mt-1.5 text-xs">
                Sélectionnez une ou plusieurs compagnies sollicitées pour cette opportunité.
                Statut initial : en attente.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-muted hover:text-brand ml-3 rounded-md p-1 hover:bg-gray-100"
              aria-label="Fermer"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>

          <div>
            <Label>Compagnies</Label>
            <MultiSelectDropdown
              options={availableCompanies.map((c) => ({ value: c.id, label: c.name }))}
              selected={selectedCompanyIds}
              onChange={setSelectedCompanyIds}
              placeholder={
                availableCompanies.length === 0
                  ? 'Toutes les compagnies actives sont déjà interrogées'
                  : 'Sélectionnez une ou plusieurs compagnies'
              }
              headerLabel="Compagnies"
              pluralLabel="compagnies"
            />
          </div>

          <div>
            <Label htmlFor="inquiry-notes">Notes (optionnel, partagées)</Label>
            <Textarea
              id="inquiry-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex. relancées par email le 15/05…"
              maxLength={2000}
            />
          </div>

          <div className="border-border-subtle flex justify-end gap-2 border-t pt-4">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              disabled={selectedCompanyIds.length === 0}
            >
              Interroger{' '}
              {selectedCompanyIds.length > 0 ? `(${selectedCompanyIds.length})` : ''}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Modale Stripe-like de saisie de la raison de refus. Pattern identique à
 * CloseLostModal / EstimatedSigningDateModal mais plus simple (1 textarea).
 */
function RefuseInquiryModal({
  inquiry,
  onClose,
  onSubmitted,
}: {
  inquiry: Inquiry;
  onClose: () => void;
  onSubmitted: () => Promise<void>;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await inquiriesApi.refuse(token, inquiry.id, reason.trim() || undefined);
      toast({
        tone: 'success',
        title: `${inquiry.insuranceCompany.name} marquée refusée`,
        message: reason.trim() ? `Motif : ${reason.trim().slice(0, 100)}` : 'Aucun motif renseigné.',
      });
      await onSubmitted();
    } catch (err) {
      toast({
        tone: 'danger',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm"
    >
      <form
        onSubmit={handleSubmit}
        className="shadow-modal w-full max-w-md space-y-5 rounded-lg bg-white p-6 animate-scale-in"
      >
        <div className="border-border-subtle border-b pb-4">
          <h2 className="text-brand text-lg font-semibold">
            Refus de {inquiry.insuranceCompany.name}
          </h2>
          <p className="text-text-muted mt-0.5 text-xs">
            La compagnie a refusé d&apos;émettre un devis. La trace reste pour les statistiques de
            la direction.
          </p>
        </div>
        <div>
          <Label htmlFor="refuse-reason">Motif (optionnel)</Label>
          <Textarea
            id="refuse-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex. secteur d'activité non couvert, prospect avec sinistralité, etc."
            maxLength={2000}
            autoFocus
          />
        </div>
        <div className="border-border-subtle flex justify-end gap-2 border-t pt-4">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Confirmer le refus
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Helper exporté pour les call-sites qui souhaitent forcer un refresh. */
export type InquiryStatusEnum = InquiryStatus;
